import { FC, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrBefore);
import { Box, CircularProgress } from '@mui/material';
import CreatedDatePicker from 'src/components/InputFields/DatePicker';
import { help, successMessages } from 'src/utils/properties.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { UpdateChequeStatusDialogProps } from 'src/models/UpdateChequeStatusDialog.interface';
import { ChequeStatus } from 'src/models/enums/ChequeStatus.enum';
import {
  CloseIcon,
  StyledDialog,
} from 'src/components/DialogBox/DialogBox.styles';
import {
  ScrollableContent,
  FormContainer,
  FieldLabel,
  RadioGroup,
  RadioOption,
  RadioCircle,
  RadioLabel,
  FixedButtonContainer,
  UpdateButton,
  CancelButton,
  HeaderContainer,
  HeaderContent,
  AlertIcon,
  HeaderTitle,
  HeaderDivider,
  ErrorMessage,
  DialogContainer,
  FirstFieldGroup,
  NotesFieldContainer,
  ChequeStatusContainer,
  RealizedDateFieldGroup,
  DateLabel,
  SmallValue14,
} from './UpdateChequeStatusDialog.styles';

const UpdateChequeStatusDialog: FC<UpdateChequeStatusDialogProps> = ({
  open,
  onClose,
  data,
  onSuccess,
  onUpdate,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const realizedDateRef = useRef<HTMLDivElement>(null);

  // Determine initial radio selection based on chequeStatus
  const getInitialIsRealized = () => {
    if (!data?.chequeStatus) return true; // Default to Realized
    switch (data.chequeStatus) {
      case ChequeStatus.RETURNED:
        return false; // Mark as Returned
      case ChequeStatus.REALIZED:
      case ChequeStatus.ISSUED:
      default:
        return true; // Mark as Realized (default for ISSUED and REALIZED)
    }
  };

  // Determine initial realized date based on chequeStatus
  const getInitialRealizedDate = () => {
    if (data?.chequeRealizedDate) return data.chequeRealizedDate;
    // For ISSUED status, default to today since Realized is selected by default
    if (data?.chequeStatus === ChequeStatus.ISSUED || !data?.chequeStatus) {
      return dayjs().format('YYYY-MM-DD');
    }
    return null;
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: {
      chequeDate: data?.chequeDate || dayjs().format('YYYY-MM-DD'),
      isRealized: getInitialIsRealized(),
      chequeRealizedDate: getInitialRealizedDate(),
    },
    validationSchema: Yup.object().shape({
      isRealized: Yup.boolean().required(),
      chequeRealizedDate: Yup.string().when(
        'isRealized',
        ([isRealized], schema) =>
          isRealized
            ? schema
                .required(help.CHEQUE_REALIZED_DATE_REQUIRED)
                .test(
                  'not-future',
                  help.CHEQUE_REALIZED_DATE_FUTURE,
                  function (value) {
                    if (!value) return true;
                    const selectedDate = dayjs(value).startOf('day');
                    const today = dayjs().startOf('day');
                    return selectedDate.isSameOrBefore(today);
                  }
                )
            : schema.nullable()
      ),
    }),
    onSubmit: async (values) => {
      if (!data?.id) return;

      try {
        const payload = {
          id: data.id,
          chequeStatus: values.isRealized
            ? ChequeStatus.REALIZED
            : ChequeStatus.RETURNED,
          chequeRealizedDate: values.isRealized
            ? values.chequeRealizedDate
              ? dayjs(values.chequeRealizedDate).format('YYYY-MM-DD')
              : null
            : null,
        };

        const response = await onUpdate(payload);
        showSnackbar(
          response.message ||
            successMessages.CHEQUE_STATUS_UPDATED_SUCCESSFULLY,
          'success'
        );
        onClose();
        onSuccess && onSuccess();
      } catch (error: unknown) {
        const errorMessage = await getApiErrorMessage(error);
        showSnackbar(errorMessage, 'error');
      }
    },
  });

  // Wrap formik in a ref to avoid stale closures in useEffect
  const formikRef = useRef(formik);
  formikRef.current = formik;

  useEffect(() => {
    if (!open) {
      formikRef.current.resetForm();
    } else {
      // Mark fields as touched on open to show validation errors immediately
      formikRef.current.setFieldTouched('chequeDate', true);
      // If incoming data already has a realized date, reflect that in the form
      if (data?.chequeRealizedDate) {
        formikRef.current.setFieldValue('isRealized', true);
        formikRef.current.setFieldValue(
          'chequeRealizedDate',
          data.chequeRealizedDate
        );
        formikRef.current.setFieldTouched('chequeRealizedDate', true);
      } else if (formikRef.current.values.isRealized) {
        formikRef.current.setFieldTouched('chequeRealizedDate', true);
      }
    }
  }, [open, data?.chequeRealizedDate]);

  useEffect(() => {
    if (formik.values.isRealized && realizedDateRef.current) {
      setTimeout(() => {
        realizedDateRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
      // Mark as touched when field becomes visible
      formikRef.current.setFieldTouched('chequeRealizedDate', true);
    } else {
      // Clear touched state and errors when not realized
      formikRef.current.setFieldTouched('chequeRealizedDate', false);
      formikRef.current.setFieldError('chequeRealizedDate', undefined);
    }
  }, [formik.values.isRealized]);

  // Trigger validation immediately when dates change
  useEffect(() => {
    if (formik.values.chequeRealizedDate) {
      formikRef.current.setFieldTouched('chequeRealizedDate', true, true);
    }
  }, [formik.values.chequeRealizedDate]);

  return (
    <StyledDialog open={open} onClose={onClose} width="724px" height="540px">
      <DialogContainer>
        <HeaderContainer>
          <HeaderContent>
            <AlertIcon component="i" className="bx bx-error-circle" />
            <HeaderTitle>Update Cheque Status</HeaderTitle>
          </HeaderContent>
          <HeaderDivider />
          <CloseIcon
            component="i"
            className="bx bx-x-circle"
            onClick={onClose}
          />
        </HeaderContainer>
        <ScrollableContent>
          <FormContainer>
            <form onSubmit={formik.handleSubmit}>
              <FirstFieldGroup>
                <FieldLabel>Cheque Date</FieldLabel>
                <DateLabel>
                  {data?.chequeDate
                    ? dayjs(data.chequeDate).format('DD MMM YYYY')
                    : 'N/A'}
                </DateLabel>
              </FirstFieldGroup>

              <NotesFieldContainer>
                <FieldLabel>Cheque Number</FieldLabel>
                <SmallValue14>{data?.chequeNumber || 'N/A'}</SmallValue14>
              </NotesFieldContainer>

              <ChequeStatusContainer>
                <FieldLabel>Cheque realized or returned?</FieldLabel>
                <RadioGroup>
                  <RadioOption
                    selected={formik.values.isRealized as boolean}
                    onClick={() => {
                      formik.setFieldValue('isRealized', true);
                      if (!formik.values.chequeRealizedDate) {
                        formik.setFieldValue(
                          'chequeRealizedDate',
                          dayjs().format('YYYY-MM-DD')
                        );
                      }
                    }}
                  >
                    <RadioCircle
                      selected={formik.values.isRealized as boolean}
                    />
                    <RadioLabel selected={formik.values.isRealized as boolean}>
                      Realized
                    </RadioLabel>
                  </RadioOption>
                  <RadioOption
                    selected={!(formik.values.isRealized as boolean)}
                    onClick={() => formik.setFieldValue('isRealized', false)}
                  >
                    <RadioCircle
                      selected={!(formik.values.isRealized as boolean)}
                    />
                    <RadioLabel
                      selected={!(formik.values.isRealized as boolean)}
                    >
                      Returned
                    </RadioLabel>
                  </RadioOption>
                </RadioGroup>
              </ChequeStatusContainer>

              {formik.values.isRealized && (
                <RealizedDateFieldGroup ref={realizedDateRef}>
                  <FieldLabel>Realized Date</FieldLabel>
                  <CreatedDatePicker
                    name="chequeRealizedDate"
                    formikProps={formik}
                    height="35px"
                    width="200px"
                    fontSize="14px"
                  />
                  {formik.touched.chequeRealizedDate &&
                    formik.errors.chequeRealizedDate && (
                      <ErrorMessage>
                        <Box component="i" className="bx bxs-error-circle" />
                        {formik.errors.chequeRealizedDate}
                      </ErrorMessage>
                    )}
                </RealizedDateFieldGroup>
              )}
            </form>
          </FormContainer>
        </ScrollableContent>
        <FixedButtonContainer>
          <UpdateButton
            type="button"
            onClick={() => formik.submitForm()}
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? (
              <CircularProgress size={16} sx={{ color: '#FFFFFF' }} />
            ) : (
              'Update'
            )}
          </UpdateButton>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
        </FixedButtonContainer>
      </DialogContainer>
    </StyledDialog>
  );
};

export default UpdateChequeStatusDialog;
